import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation, Clock } from 'lucide-react';

const INDIA_CENTER = { lat: 20.5937, lng: 78.9629 };

const dot = (color) => L.divIcon({
  className: 'route-marker',
  html: `<div style="width:16px;height:16px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`,
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});
const pickupIcon = dot('#0f172a');
const dropIcon = dot('#16a34a');

function FitBounds({ points }) {
  const map = useMap();
  const key = points.map((p) => p.join(',')).join(';');
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 14);
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);
  return null;
}

export default function RouteMap({ pickup, drop, route, fallbackCenter, height = 480 }) {
  const center = pickup || drop || fallbackCenter || INDIA_CENTER;
  const zoom = pickup || drop || fallbackCenter ? 13 : 5;
  const line = route?.geometry?.coordinates?.map(([lng, lat]) => [lat, lng]) || [];
  const points = line.length ? line : [pickup, drop].filter(Boolean).map((p) => [p.lat, p.lng]);

  return (
    <div className="relative z-0" style={{ height, width: '100%' }} data-testid="route-map">
      <MapContainer center={[center.lat, center.lng]} zoom={zoom} scrollWheelZoom={false}
        style={{ height: '100%', width: '100%' }} attributionControl={false}>
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {pickup && (
          <Marker position={[pickup.lat, pickup.lng]} icon={pickupIcon}>
            <Tooltip direction="top" offset={[0, -10]}>Pickup</Tooltip>
          </Marker>
        )}
        {drop && (
          <Marker position={[drop.lat, drop.lng]} icon={dropIcon}>
            <Tooltip direction="top" offset={[0, -10]}>Drop</Tooltip>
          </Marker>
        )}
        {line.length > 0 && <Polyline positions={line} pathOptions={{ color: '#2563eb', weight: 5, opacity: 0.85 }} />}
        <FitBounds points={points} />
      </MapContainer>
      {route && (
        <div className="absolute top-3 right-3 z-[500] bg-white/95 backdrop-blur rounded-lg shadow px-3 py-2 text-sm flex items-center gap-3"
          data-testid="route-summary">
          <span className="flex items-center gap-1 font-semibold"><Navigation className="w-4 h-4 text-primary" />{route.distance_km} km</span>
          <span className="flex items-center gap-1 text-muted-foreground"><Clock className="w-4 h-4" />~{route.duration_min} min</span>
        </div>
      )}
      {!pickup && !drop && (
        <div className="absolute inset-x-0 bottom-3 z-[500] flex justify-center pointer-events-none">
          <span className="bg-white/95 rounded-full shadow px-4 py-1.5 text-xs text-muted-foreground" data-testid="route-map-hint">
            Enter pickup and drop locations to see your route
          </span>
        </div>
      )}
      <span className="absolute bottom-1 right-2 z-[500] text-[10px] text-muted-foreground bg-white/70 px-1 rounded">© OpenStreetMap contributors</span>
    </div>
  );
}
